import { useEffect, useState, useCallback } from "react";
import { apiEmbalei, apiIDW } from "../../Services/api";
import { format } from "date-fns";
import {
  globalCSS,
  page,
  dash,
  header,
  distribution,
  cards,
  metrics,
  glossary,
  footer,
  colors,
  compareState,
} from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
const ORDER_TYPES = "4410,2967,4412,4041,5440,4293,890,875,877,871";
const PAGE_SIZE = 2000;
const REFRESH_MS = 5 * 60 * 1000; // Atualiza a cada 5 minutos

const STAGE_KEYS = ["inicio", "separacao", "embalagem", "embarque"];
const STAGE_LABELS = {
  inicio: "AG. INÍCIO",
  separacao: "EM SEPARAÇÃO",
  embalagem: "AG. EMBALAGEM",
  embarque: "AG. EMBARQUE",
};

// ─────────────────────────────────────────────────────────────────────────────
// Calendário de feriados
// ─────────────────────────────────────────────────────────────────────────────
const FIXED_HOLIDAYS = new Set([
  "01-01", "04-21", "05-01", "09-07", "10-12",
  "11-02", "11-15", "11-20", "12-25",
]);

const SPECIFIC_HOLIDAYS = new Set([
  "2026-02-16", "2026-02-17", "2026-04-03", "2026-05-14",
]);

function isHoliday(date) {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const mmdd = `${mm}-${dd}`;
  const full = `${date.getFullYear()}-${mmdd}`;
  return FIXED_HOLIDAYS.has(mmdd) || SPECIFIC_HOLIDAYS.has(full);
}

// ─────────────────────────────────────────────────────────────────────────────
// Jornada de trabalho
//   Seg–Sex 06:00–19:00 (780 min) · Sáb 06:00–14:20 (500 min) · Dom/Feriado 0
// ─────────────────────────────────────────────────────────────────────────────
const WORK_BOUNDS = {
  weekday: { startMin: 6 * 60, endMin: 19 * 60 },
  saturday: { startMin: 6 * 60, endMin: 14 * 60 + 20 },
};

function getWorkBoundsForDay(date) {
  const day = date.getDay();
  if (day === 0 || isHoliday(date)) return null;
  if (day === 6) return WORK_BOUNDS.saturday;
  return WORK_BOUNDS.weekday;
}

function minutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

function workingMinutesInInterval(date, fromMin, toMin) {
  const bounds = getWorkBoundsForDay(date);
  if (!bounds) return 0;
  const start = Math.max(fromMin, bounds.startMin);
  const end = Math.min(toMin, bounds.endMin);
  return Math.max(0, end - start);
}

// ─────────────────────────────────────────────────────────────────────────────
// Datas
// ─────────────────────────────────────────────────────────────────────────────
function parseDate(str) {
  if (!str) return null;
  if (str instanceof Date) return str;
  const s = String(str).replace("T", " ").replace("Z", "").split(".")[0];
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi, sec] = m;
  return new Date(+y, +mo - 1, +d, +h, +mi, +sec);
}

function diffWorkingMinutes(startRaw, endRaw) {
  const s = parseDate(startRaw);
  const e = parseDate(endRaw);
  if (!s || !e || e <= s) return null;

  const startDate = new Date(s);
  const endDate = new Date(e);
  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  let total = 0;

  if (startDay.getTime() === endDay.getTime()) {
    total = workingMinutesInInterval(startDate, minutesOfDay(startDate), minutesOfDay(endDate));
  } else {
    const sb = getWorkBoundsForDay(startDate);
    if (sb) total += workingMinutesInInterval(startDate, minutesOfDay(startDate), sb.endMin);

    const cur = new Date(startDay);
    cur.setDate(cur.getDate() + 1);
    while (cur.getTime() < endDay.getTime()) {
      const b = getWorkBoundsForDay(cur);
      if (b) total += b.endMin - b.startMin;
      cur.setDate(cur.getDate() + 1);
    }

    const eb = getWorkBoundsForDay(endDate);
    if (eb) total += workingMinutesInInterval(endDate, eb.startMin, minutesOfDay(endDate));
  }

  if (total <= 0) return 0;
  return Math.max(1, Math.round(total));
}

function getLocalDateStr() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now - offset).toISOString().split("T")[0];
}

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
const LOG_DELAY_MS = 500; // espaçamento entre cada DISPATCH de /log
const EMBALEI_DELAY_MS = 500; // espaçamento entre cada DISPATCH de batch de embalei
const LOG_CHUNK_SIZE = 30; // pedidos por chamada batched de /log
const EMBALEI_BATCH_SIZE = 30; // chamadas de embalei disparadas em paralelo por batch

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cálculo de ciclo
// ─────────────────────────────────────────────────────────────────────────────
function computeOrderStageTimes(logs, embaleiData, order) {
  if (!Array.isArray(logs) || logs.length === 0) return {};
  const result = {};

  const stamp = (data) => ({
    ...data,
    IDOrder: order?.IDOrder ?? null,
    Order: order?.Order ?? null,
    QuantitySku: order?.QuantitySku ?? null,
  });

  // Identificação por IDEvent quando único; Comments (case-insensitive) só
  // para distinguir as duas variantes de IDEvent=35 (entrada em separação x romaneio).
  const finalizarEvt = logs.find((e) => e.IDEvent === 2) || null;
  const expedidoEvt = logs.find((e) => e.IDEvent === 4) || null;

  const matchTransitionTo = (target) => (e) =>
    e.IDEvent === 35 &&
    (e.Comments || "").toLowerCase().includes(`para: ${target}`.toLowerCase());

  const paraEmSepEvt = logs.find(matchTransitionTo("em separação")) || null;
  const paraRomaneioEvt = logs.find(matchTransitionTo("aguardando romaneio")) || null;

  if (finalizarEvt && paraEmSepEvt) {
    const minutes = diffWorkingMinutes(finalizarEvt.RecordTimestamp, paraEmSepEvt.RecordTimestamp);
    if (minutes !== null) result.inicio = stamp({ minutes, endAt: paraEmSepEvt.RecordTimestamp });
  }

  if (paraEmSepEvt && paraRomaneioEvt) {
    const minutes = diffWorkingMinutes(paraEmSepEvt.RecordTimestamp, paraRomaneioEvt.RecordTimestamp);
    if (minutes !== null) result.separacao = stamp({ minutes, endAt: paraRomaneioEvt.RecordTimestamp });
  }

  // AG. EMBALAGEM: paraRomaneio → created_at (momento da embalagem, NÃO created_at)
  if (paraRomaneioEvt && embaleiData?.created_at) {
    const minutes = diffWorkingMinutes(paraRomaneioEvt.RecordTimestamp, embaleiData.created_at);
    if (minutes !== null) result.embalagem = stamp({ minutes, endAt: embaleiData.created_at });
  }

  // AG. EMBARQUE: created_at → expedido
  if (embaleiData?.created_at && expedidoEvt) {
    const minutes = diffWorkingMinutes(embaleiData.created_at, expedidoEvt.RecordTimestamp);
    if (minutes !== null) result.embarque = stamp({ minutes, endAt: expedidoEvt.RecordTimestamp });
  }

  return result;
}

function computeStageStats(allStageTimes) {
  const stats = {};
  for (const stageKey of STAGE_KEYS) {
    const entries = allStageTimes
      .map((o) => o[stageKey])
      .filter((e) => e && e.minutes !== null);

    if (!entries.length) {
      stats[stageKey] = {
        media: null,
        mediaUltimos10: null,
        paceEntries: [],
        ultimos10Count: 0,
        total: 0,
        avgSku: null,
      };
      continue;
    }

    // Média geral nesta etapa (todos os pedidos calculados)
    const media = entries.reduce((s, e) => s + e.minutes, 0) / entries.length;

    // Média dos 10 pedidos mais recentes (por endAt desc)
    const sortedDesc = [...entries].sort(
      (a, b) => parseDate(b.endAt) - parseDate(a.endAt)
    );
    const ultimos10 = sortedDesc.slice(0, 10);
    const mediaUltimos10 =
      ultimos10.reduce((s, e) => s + e.minutes, 0) / ultimos10.length;

    // Média de SKU/pedido considerando todos os pedidos calculados na etapa
    const skuValues = entries
      .map((e) => Number(e.QuantitySku))
      .filter((v) => Number.isFinite(v));
    const avgSku = skuValues.length
      ? skuValues.reduce((a, b) => a + b, 0) / skuValues.length
      : null;

    stats[stageKey] = {
      media,
      mediaUltimos10,
      // Lista de entries em que cada pedido saiu desta etapa — usada para
      // calcular o "tempo de execução" (ritmo) e a média de SKU/pedido na
      // mesma janela.
      paceEntries: entries.map((e) => ({
        endAt: e.endAt,
        QuantitySku: e.QuantitySku ?? null,
      })),
      ultimos10Count: ultimos10.length,
      total: entries.length,
      avgSku,
    };
  }
  return stats;
}

// Tempo de execução = janela / N, onde N = quantidade de pedidos que saíram
// desta etapa nos últimos `windowMin` minutos úteis. Equivale a "1 pedido a
// cada X min úteis" no período. Também devolve a média de SKU por pedido
// considerando o mesmo conjunto de pedidos da janela.
// Retorna { every, count, avgSku } ou null se a janela for inválida /
// nenhum pedido saiu no período.
function computePace(paceEntries, windowMin, now) {
  if (!Array.isArray(paceEntries) || paceEntries.length === 0) return null;
  if (!windowMin || windowMin <= 0) return null;

  let count = 0;
  let skuSum = 0;
  let skuCount = 0;
  for (const entry of paceEntries) {
    const diff = diffWorkingMinutes(entry.endAt, now);
    if (diff !== null && diff <= windowMin) {
      count++;
      const q = Number(entry.QuantitySku);
      if (Number.isFinite(q)) {
        skuSum += q;
        skuCount++;
      }
    }
  }
  if (count === 0) return null;
  return {
    every: windowMin / count,
    count,
    avgSku: skuCount > 0 ? skuSum / skuCount : null,
  };
}

// MÉDIA DE EXECUÇÃO POR PEDIDO + EXECUÇÃO DO ÚLTIMO PEDIDO (ciclo total)
//   Considera apenas pedidos com as 4 etapas concluídas.
function computeCycleTotals(allStageTimes) {
  const completos = allStageTimes.filter((o) =>
    STAGE_KEYS.every((k) => o[k] && o[k].minutes != null)
  );

  if (!completos.length) return { media: null, ultimo: null, total: 0 };

  const totals = completos.map((o) =>
    STAGE_KEYS.reduce((acc, k) => acc + o[k].minutes, 0)
  );

  const media = Math.round(totals.reduce((a, b) => a + b, 0) / totals.length);

  const latest = completos.reduce((a, b) =>
    parseDate(b.embarque.endAt) > parseDate(a.embarque.endAt) ? b : a
  );
  const ultimo = STAGE_KEYS.reduce((acc, k) => acc + latest[k].minutes, 0);

  return {
    media,
    ultimo,
    ultimoIDOrder: latest.embarque.IDOrder ?? null,
    ultimoOrder: latest.embarque.Order ?? null,
    ultimoEndAt: latest.embarque.endAt ?? null,
    total: completos.length,
  };
}

function needsEmbalei(order) {
  return order.IDStatusOrder === 22 || Boolean(order.ShippingDate);
}

async function fetchAllPages(params) {
  let page = 1;
  let all = [];
  let hasMore = true;
  while (hasMore) {
    const response = await apiIDW.get("/orders", { params: { ...params, page } });
    const data = Array.isArray(response.data) ? response.data : [];
    all = all.concat(data);
    hasMore = data.length >= PAGE_SIZE;
    page += 1;
  }
  return all;
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatação de tempo
//   Entradas SEMPRE em minutos (com casas decimais permitidas).
// ─────────────────────────────────────────────────────────────────────────────
const pad2 = (n) => String(n).padStart(2, "0");

// Formatador padrão para tempo (entrada em minutos com casas decimais):
//   ≥ 1h  → "HH:MM"  + unidade "h"
//   ≥ 1m  → "MM:SS"  + unidade "min"
//   < 1m  → "00:SS"  + unidade "seg"
function fmtCard(mins) {
  if (mins == null || isNaN(mins)) return { value: "—", unit: "" };
  const totalSec = Math.max(0, Math.round(Number(mins) * 60));

  if (totalSec >= 3600) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    return { value: `${pad2(h)}:${pad2(m)}`, unit: "h" };
  }

  if (totalSec >= 60) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return { value: `${pad2(m)}:${pad2(s)}`, unit: "min" };
  }

  return { value: `00:${pad2(totalSec)}`, unit: "seg" };
}

// Subtítulo dos cards de RITMO: dois bloquinhos empilhados
//   1º: "X pedidos no período"
//   2º: "Y SKU/pedido"  (arredondado para inteiro)
function buildPaceSubtitle(pace) {
  if (!pace || pace.count == null) return null;
  const lines = [
    `${pace.count.toLocaleString("pt-BR")} pedidos no período`,
  ];
  if (pace.avgSku != null) {
    lines.push(`${Math.round(pace.avgSku).toLocaleString("pt-BR")} SKU/pedido`);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      {lines.map((line, i) => (
        <div key={i} style={metrics.compare("equal")}>{line}</div>
      ))}
    </div>
  );
}

// Versão "inline" do fmtCard — devolve a string com unidade já concatenada.
// Usada nas legendas de comparação ("00:49 h abaixo" / "15:30 min acima").
function fmtHMS(mins) {
  if (mins == null || isNaN(mins)) return "—";
  const c = fmtCard(mins);
  return c.unit ? `${c.value} ${c.unit}` : c.value;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componentes
// ─────────────────────────────────────────────────────────────────────────────
function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    let timer;
    function scheduleNext() {
      const delay = 1000 - (Date.now() % 1000);
      timer = setTimeout(() => {
        setTime(new Date());
        scheduleNext();
      }, delay);
    }
    scheduleNext();
    return () => clearTimeout(timer);
  }, []);
  return <div style={header.clock}>{time.toLocaleTimeString("pt-BR")}</div>;
}

function Metric({ idx, label, value, baseline, showCompare, subtitle }) {
  const state = showCompare ? compareState(value, baseline) : null;
  let arrow = null;
  let compareLabel = null;
  if (state) {
    const diff = Math.abs(value - baseline);
    if (state === "below") {
      arrow = "▼";
      compareLabel = `${fmtHMS(diff)} abaixo`;
    } else if (state === "above") {
      arrow = "▲";
      compareLabel = `${fmtHMS(diff)} acima`;
    } else {
      arrow = "=";
      compareLabel = "na média";
    }
  }

  const fc = fmtCard(value);

  return (
    <div style={metrics.cell(idx)}>
      <div style={metrics.label}>{label}</div>
      <div style={metrics.value(state)}>
        {fc.value}
        {value != null && fc.unit && <span style={metrics.unit}>{fc.unit}</span>}
      </div>
      {showCompare && state && (
        <div style={metrics.compare(state)}>
          <span style={metrics.compareArrow}>{arrow}</span>
          {compareLabel}
        </div>
      )}
      {subtitle}
    </div>
  );
}

function StageCard({ idx, stageKey, stageStats }) {
  return (
    <article style={cards.card}>
      <div style={cards.accent(idx)} />
      <div style={cards.head}>
        <span style={cards.stageTag}>ETAPA {idx + 1} DE 4</span>
        <h3 style={cards.title(idx)}>{STAGE_LABELS[stageKey]}</h3>
        <div style={cards.count}>
          <strong style={cards.countStrong}>
            {Number(stageStats.total ?? 0).toLocaleString("pt-BR")}
          </strong>{" "}
          pedidos calculados
          {stageStats.avgSku != null && (
            <>
              {" - "}
              <strong style={cards.countStrong}>
                {Math.round(stageStats.avgSku).toLocaleString("pt-BR")}
              </strong>{" "}
              SKU/pedido
            </>
          )}
        </div>
      </div>

      <div style={metrics.grid}>
        <Metric
          idx={0}
          label="MÉDIA DE TEMPO DE EXECUÇÃO"
          value={stageStats.mediaTempoExecucao?.every}
          subtitle={buildPaceSubtitle(stageStats.mediaTempoExecucao)}
        />
        <Metric
          idx={1}
          label="TEMPO DE EXECUÇÃO DOS ÚLTIMOS PEDIDOS"
          value={stageStats.tempoExecucaoUltimos10?.every}
          baseline={stageStats.mediaTempoExecucao?.every}
          showCompare
          subtitle={buildPaceSubtitle(stageStats.tempoExecucaoUltimos10)}
        />
        <Metric
          idx={2}
          label="MÉDIA DE TEMPO NESTA ETAPA"
          value={stageStats.media}
        />
        <Metric
          idx={3}
          label={`MÉDIA NESTA ETAPA DOS ÚLTIMOS ${stageStats.ultimos10Count ?? 10} PEDIDOS`}
          value={stageStats.mediaUltimos10}
          baseline={stageStats.media}
          showCompare
        />
      </div>
    </article>
  );
}

function DistributionPanel({ stats, cycleTotal }) {
  const total =
    STAGE_KEYS.reduce((s, k) => s + (stats[k]?.media ?? 0), 0) || 0;

  return (
    <section style={distribution.wrap}>
      <div style={distribution.head}>
        <h2 style={distribution.title}>DISTRIBUIÇÃO DO TEMPO NO CICLO</h2>
        <div style={distribution.totalBlock}>
          <span style={distribution.totalLabel}>CICLO TOTAL MÉDIO</span>
          <span style={distribution.totalValue}>
            {(() => {
              const fc = fmtCard(cycleTotal);
              return cycleTotal == null ? "—" : `${fc.value} ${fc.unit}`;
            })()}
          </span>
        </div>
      </div>

      <div style={distribution.grid}>
        {STAGE_KEYS.map((key, i) => {
          const v = stats[key]?.media;
          const pct = total > 0 && v != null ? (v / total) * 100 : 0;
          const fc = fmtCard(v);
          return (
            <div key={key} style={distribution.stage(i)}>
              <div style={distribution.stageLabel(i)}>{STAGE_LABELS[key]}</div>
              <div style={distribution.stageValue}>
                {fc.value}
                {v != null && fc.unit && <span style={distribution.stageUnit}>{fc.unit}</span>}
              </div>
              <div style={distribution.stagePct}>
                {total > 0 && v != null ? `${pct.toFixed(0)}% do ciclo total` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      <div style={distribution.bar}>
        {STAGE_KEYS.map((key, i) => {
          const v = stats[key]?.media ?? 0;
          const width = total > 0 ? (v / total) * 100 : 0;
          return <div key={key} style={distribution.barSeg(i, width)} />;
        })}
      </div>
    </section>
  );
}

function GlossaryPanel() {
  const items = [
    {
      label: "MÉDIA DE TEMPO DE EXECUÇÃO",
      help: "Tempo por pedido = média da etapa ÷ pedidos que saíram nos últimos M min úteis (M = média da etapa)",
    },
    {
      label: "TEMPO DE EXECUÇÃO DOS ÚLTIMOS PEDIDOS",
      help: "Tempo por pedido = média dos últimos 10 ÷ pedidos que saíram nos últimos M min úteis (M = média dos últimos 10)",
    },
    {
      label: "MÉDIA DE TEMPO NESTA ETAPA",
      help: "Tempo médio de TODOS os pedidos que passaram nesta etapa",
    },
    {
      label: "MÉDIA NESTA ETAPA DOS ÚLTIMOS 10 PEDIDOS",
      help: "Soma dos tempos dos 10 pedidos mais recentes dividida por 10",
    },
  ];

  return (
    <section style={glossary.wrap}>
      <div style={glossary.head}>
        <h3 style={glossary.title}>GLOSSÁRIO DE MÉTRICAS</h3>
        <span style={glossary.sub}>o que cada número significa</span>
      </div>
      <div style={glossary.grid}>
        {items.map((it) => (
          <div key={it.label} style={glossary.item}>
            <div style={glossary.label}>{it.label}</div>
            <div style={glossary.help}>{it.help}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FooterBar() {
  return (
    <footer style={footer.wrap}>
      <div style={footer.legend}>
        {STAGE_KEYS.map((key, i) => (
          <div key={key} style={footer.legendItem}>
            <span style={footer.swatch(colors.stages[i])} />
            {STAGE_LABELS[key]}
          </div>
        ))}
        <div style={footer.divider} />
        <div style={{ ...footer.legendItem, ...footer.legendCompare }}>
          <span style={footer.swatch(colors.good)} /> Último pedido abaixo da média
        </div>
        <div style={{ ...footer.legendItem, ...footer.legendCompare }}>
          <span style={footer.swatch(colors.bad)} /> Último pedido acima da média
        </div>
      </div>
      <div style={footer.schedule}>
        Minutos úteis · Seg–Sex 06:00–19:00 · Sáb 06:00–14:20 · Atualiza a cada 30 min
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
function CicloTempo() {
  const [stats, setStats] = useState(() =>
    STAGE_KEYS.reduce((acc, k) => {
      acc[k] = {
        media: null,
        mediaUltimos10: null,
        paceEntries: [],
        ultimos10Count: 0,
        total: 0,
        avgSku: null,
      };
      return acc;
    }, {})
  );
  const [cycleTotals, setCycleTotals] = useState({ media: null, ultimo: null, total: 0 });

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0 });

    try {
      const todayStr = getLocalDateStr();

      const [rawInPicking, rawAwaitingManifest, rawShipped] = await Promise.all([
        fetchAllPages({ IDStatusOrder: "20", IDOrderType: ORDER_TYPES }),
        fetchAllPages({ IDStatusOrder: "22", IDOrderType: ORDER_TYPES }),
        fetchAllPages({ IDOrderType: ORDER_TYPES, ShippingDateFrom: todayStr, ShippingDateTo: todayStr }),
      ]);

      const orderMap = new Map();
      const addIfNew = (list) =>
        list.forEach((o) => {
          if (!orderMap.has(o.IDOrder)) orderMap.set(o.IDOrder, o);
        });
      addIfNew(rawInPicking);
      addIfNew(rawAwaitingManifest);
      addIfNew(rawShipped);

      const allOrders = Array.from(orderMap.values());
      const allStageTimes = [];

      const chunks = chunkArray(allOrders, LOG_CHUNK_SIZE);
      const ordersWithEmbalei = allOrders.filter((o) => needsEmbalei(o));

      // Conta antes os disparos reais de /log para o progresso ser acurado
      let totalLogCalls = 0;
      for (const chunkOrders of chunks) {
        totalLogCalls++; // finalizar sempre
        totalLogCalls++; // status sempre (necessário p/ AG. INÍCIO de todos)
        if (chunkOrders.some((o) => Boolean(o.ShippingDate))) totalLogCalls++;
      }
      const totalWork = totalLogCalls + ordersWithEmbalei.length;
      let workDone = 0;
      setProgress({ current: 0, total: totalWork });
      const incProgress = () => {
        workDone++;
        setProgress({ current: workDone, total: totalWork });
      };

      const joinOrders = (list) => list.map((o) => o.Order).join(",");

      // Mapas globais — preenchidos pelos handlers das promises
      const finalizarByOrder = new Map(allOrders.map((o) => [o.IDOrder, []]));
      const statusByOrder = new Map(allOrders.map((o) => [o.IDOrder, []]));
      const expedidoByOrder = new Map(allOrders.map((o) => [o.IDOrder, []]));
      const failedOrders = new Set();

      // ─── FASE 1: DISPATCH DE TODOS OS LOGS ─────────────────────────────────
      //   Fire-and-forget: dispara com 500ms entre cada call, NÃO aguarda
      //   resposta antes do próximo dispatch. Timeout de uma call não bloqueia
      //   as seguintes. Aguarda todas só no Promise.allSettled final.
      //
      //   Filtros por status:
      //     Em separação (20)        → finalizar + status (necessário p/ AG. INÍCIO)
      //     Aguardando Romaneio (22) → finalizar + status + embalei
      //     Enviado (ShippingDate)   → todas
      const logPromises = [];
      let isFirstLogCall = true;

      for (let ci = 0; ci < chunks.length; ci++) {
        const chunkOrders = chunks[ci];
        // Status: TODOS os pedidos. Sem ele não dá pra fechar o tempo do AG. INÍCIO
        // dos que estão em Em separação (que são justamente os mais recentes).
        const ordersNeedingStatus = chunkOrders;
        const ordersNeedingExpedido = chunkOrders.filter((o) => Boolean(o.ShippingDate));

        // Finalizar (sempre)
        if (!isFirstLogCall) await sleep(LOG_DELAY_MS);
        isFirstLogCall = false;
        logPromises.push(
          apiIDW
            .get("/log", { params: { Order: joinOrders(chunkOrders), IDEvent: 2 } })
            .then((r) => {
              const data = Array.isArray(r.data) ? r.data : [];
              for (const e of data) {
                if (finalizarByOrder.has(e.IDOrder)) finalizarByOrder.get(e.IDOrder).push(e);
              }
            })
            .catch((err) => {
              console.warn("CicloTempo — erro log finalizar (batch):", err);
            })
            .finally(incProgress)
        );

        // Status (condicional)
        if (ordersNeedingStatus.length) {
          await sleep(LOG_DELAY_MS);
          logPromises.push(
            apiIDW
              .get("/log", { params: { Order: joinOrders(ordersNeedingStatus), IDEvent: 35 } })
              .then((r) => {
                const data = Array.isArray(r.data) ? r.data : [];
                for (const e of data) {
                  if (statusByOrder.has(e.IDOrder)) statusByOrder.get(e.IDOrder).push(e);
                }
              })
              .catch((err) => {
                console.warn("CicloTempo — erro log status (batch):", err);
                for (const o of ordersNeedingStatus) failedOrders.add(o.IDOrder);
              })
              .finally(incProgress)
          );
        }

        // Expedido (condicional)
        if (ordersNeedingExpedido.length) {
          await sleep(LOG_DELAY_MS);
          logPromises.push(
            apiIDW
              .get("/log", { params: { Order: joinOrders(ordersNeedingExpedido), IDEvent: 4 } })
              .then((r) => {
                const data = Array.isArray(r.data) ? r.data : [];
                for (const e of data) {
                  if (expedidoByOrder.has(e.IDOrder)) expedidoByOrder.get(e.IDOrder).push(e);
                }
              })
              .catch(() => {})
              .finally(incProgress)
          );
        }
      }

      // Aguarda todos os logs concluírem (sucesso, erro ou timeout) antes do embalei
      await Promise.allSettled(logPromises);

      // ─── FASE 2: DISPATCH DE EMBALEI EM BATCHES DE 15 ─────────────────────
      //   15 calls em paralelo por batch · 500ms entre dispatches de batch
      const embaleiByOrder = new Map();
      const embaleiBatches = chunkArray(ordersWithEmbalei, EMBALEI_BATCH_SIZE);
      const embaleiPromises = [];

      for (let bi = 0; bi < embaleiBatches.length; bi++) {
        if (bi > 0) await sleep(EMBALEI_DELAY_MS);
        const batch = embaleiBatches[bi];
        for (const o of batch) {
          embaleiPromises.push(
            apiEmbalei
              .get(`/orders/${o.IDOrder}`)
              .then((r) => {
                const raw = r.data;
                embaleiByOrder.set(
                  o.IDOrder,
                  Array.isArray(raw) && raw.length > 0 ? raw[0] : null
                );
              })
              .catch(() => {
                embaleiByOrder.set(o.IDOrder, null); // não embalado — silencioso
              })
              .finally(incProgress)
          );
        }
      }

      await Promise.allSettled(embaleiPromises);

      // ─── FASE 3: PROCESSAMENTO ────────────────────────────────────────────
      for (const order of allOrders) {
        if (failedOrders.has(order.IDOrder)) continue;

        const logs = [
          ...(finalizarByOrder.get(order.IDOrder) || []),
          ...(statusByOrder.get(order.IDOrder) || []),
          ...(expedidoByOrder.get(order.IDOrder) || []),
        ];
        const embaleiData = embaleiByOrder.get(order.IDOrder) || null;

        const stageTimes = computeOrderStageTimes(logs, embaleiData, order);
        if (Object.keys(stageTimes).length > 0) allStageTimes.push(stageTimes);
      }

      setStats(computeStageStats(allStageTimes));
      setCycleTotals(computeCycleTotals(allStageTimes));
      setLastUpdate(new Date());
    } catch (err) {
      console.error("CicloTempo — erro geral:", err);
      setError("Erro ao carregar dados. Verifique a conexão e tente novamente.");
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const cycleTotalAvg = cycleTotals.media;

  // Enriquece cada etapa com as taxas de execução (mesma lógica do "ritmo"):
  //   mediaTempoExecucao    → janela = média geral da etapa
  //   tempoExecucaoUltimos10 → janela = média dos últimos 10 pedidos
  // Cada uma devolve { every, count } — `every` é o tempo por pedido no
  // período e `count` é a quantidade de pedidos que saíram da etapa naquela
  // janela (exibida como subtítulo no card).
  const now = new Date();
  const enrichedStats = STAGE_KEYS.reduce((acc, k) => {
    const s = stats[k] || {};
    acc[k] = {
      ...s,
      mediaTempoExecucao: computePace(s.paceEntries, s.media, now),
      tempoExecucaoUltimos10: computePace(s.paceEntries, s.mediaUltimos10, now),
    };
    return acc;
  }, {});

  const statusLabel = loading
    ? `ATUALIZANDO ${progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%`
    : lastUpdate
    ? `ATUALIZADO ${format(lastUpdate, "HH:mm")}`
    : "AGUARDANDO DADOS";

  return (
    <>
      <style>{globalCSS}</style>

      <div style={page}>
        <div style={dash}>

          {/* HEADER */}
          <header style={header.wrap}>
            <div>
              <h1 style={header.title}>TEMPO DE CICLO</h1>
              <p style={header.subtitle}>
                Monitor de etapas do pedido · Em separação · Ag. romaneio · Enviados hoje
              </p>
            </div>
            <div style={header.right}>
              <div
                onClick={loading ? undefined : fetchData}
                style={header.pill(loading)}
                title={loading ? "Atualizando..." : "Clique para atualizar agora"}
              >
                <span style={header.pillDot(loading)} />
                <span>{statusLabel}</span>
              </div>
              <Clock />
            </div>
          </header>

          {/* ERRO */}
          {error && (
            <div
              style={{
                background: colors.badSoft,
                border: `1px solid ${colors.bad}`,
                borderRadius: 8,
                padding: "12px 18px",
                color: colors.bad,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              ⚠ {error}
            </div>
          )}

          {/* DISTRIBUIÇÃO */}
          <DistributionPanel stats={enrichedStats} cycleTotal={cycleTotalAvg} />

          {/* CARDS */}
          <section style={cards.grid}>
            {STAGE_KEYS.map((key, i) => (
              <StageCard
                key={key}
                idx={i}
                stageKey={key}
                stageStats={enrichedStats[key]}
              />
            ))}
          </section>

          {/* GLOSSÁRIO */}
          <GlossaryPanel />

          {/* FOOTER */}
          <FooterBar />
        </div>
      </div>
    </>
  );
}

export default CicloTempo;
